/**
 * Indian money formatting — the lakh/crore digit grouping (spec §3, §8.2).
 *
 *   ₹1,45,600.00   correct — 3 digits, then pairs
 *   ₹145,600.00    WRONG — western grouping
 *
 * The owner reads western grouping as a mistake in the software, so this is a
 * correctness concern, not a cosmetic one.
 *
 * WHY THIS IS HAND-ROLLED RATHER THAN `toLocaleString("en-IN")`:
 * `Intl` grouping depends on the ICU data compiled into the runtime. A Node built
 * with `small-icu` resolves `en-IN` to the `en` fallback and silently emits
 * WESTERN grouping — no error, no warning, just wrong invoices. Since the PDF is
 * rendered server-side (spec §1), that failure would land on the printed document
 * on the VPS while looking perfect on the dev machine. Explicit integer digit
 * grouping cannot fail that way and is identical on every runtime.
 *
 * Money arrives as integer paise (spec §3) and never passes through a float.
 */
import { PAISE_PER_RUPEE, assertInteger } from "./rounding.js";
/** The rupee sign. */
export const RUPEE_SIGN = "₹";
/**
 * Group a run of digits the Indian way: the last three, then pairs.
 *
 * ```
 *      600 -> "600"
 *     6750 -> "6,750"
 *   145600 -> "1,45,600"
 * 12345678 -> "1,23,45,678"
 * ```
 *
 * @param digits Digits only — no sign, no decimal point, no separators.
 */
export function groupIndianDigits(digits) {
    if (!/^[0-9]+$/.test(digits)) {
        throw new Error(`groupIndianDigits expects digits only, received "${digits}"`);
    }
    if (digits.length <= 3)
        return digits;
    const lastThree = digits.slice(-3);
    let rest = digits.slice(0, -3);
    const pairs = [];
    while (rest.length > 2) {
        pairs.unshift(rest.slice(-2));
        rest = rest.slice(0, -2);
    }
    if (rest.length > 0)
        pairs.unshift(rest);
    return `${pairs.join(",")},${lastThree}`;
}
/**
 * Format integer paise as an Indian-grouped rupee amount.
 *
 * ```
 * formatPaise(675000)                    === "6,750.00"
 * formatPaise(14560000)                  === "1,45,600.00"
 * formatPaise(14560000, { symbol: true }) === "₹1,45,600.00"
 * formatPaise(14560000, { decimals: 0 }) === "1,45,600"
 * formatPaise(-25000)                    === "-250.00"
 * ```
 *
 * A negative amount keeps an ASCII hyphen; `round_off_paise` is the only value in
 * the system that is legitimately negative (spec §4).
 */
export function formatPaise(paise, options = {}) {
    assertInteger(paise, "formatPaise amount");
    const { symbol = false, decimals = 2 } = options;
    if (decimals !== 0 && decimals !== 2) {
        throw new Error(`formatPaise decimals must be 0 or 2, received ${String(decimals)}`);
    }
    const negative = paise < 0;
    const absolute = Math.abs(paise);
    const whole = Math.floor(absolute / PAISE_PER_RUPEE);
    const fraction = absolute % PAISE_PER_RUPEE;
    // decimals: 0 truncates toward zero — it is a display-only convenience and must
    // never be used for a persisted or printed document total.
    const body = decimals === 0
        ? groupIndianDigits(String(whole))
        : `${groupIndianDigits(String(whole))}.${String(fraction).padStart(2, "0")}`;
    return `${negative ? "-" : ""}${symbol ? RUPEE_SIGN : ""}${body}`;
}
/**
 * Convenience: `formatPaise` with the rupee sign and two decimals — the form used
 * on the invoice and the quotation.
 *
 * ```
 * formatRupees(1298000) === "₹12,980.00"
 * ```
 */
export function formatRupees(paise) {
    return formatPaise(paise, { symbol: true, decimals: 2 });
}
//# sourceMappingURL=format-money.js.map